// ft3d-emote-clips.ts — chat emote → avatar clip (owner 2026-09-16).
//
// THE ONE MAP between what a player types (/wave, /taunt, /dance) and the clip the avatar plays.
// Candidates are tried in order and the FIRST one the rig actually has wins; an emote with no clip
// present simply does not animate — the line, the bubble and the voice still land. That is why this
// file can name clips before they are promoted: nothing breaks while a family is missing.
//
// ⚠ Names here must match the PRODUCTION clip names, which are PascalCase semantic names
// (`StandingIdle`, `OneHandSlashHorizontal`, `Turn_neutral_right_90`) — NOT the kebab-case names the
// audition packs ship (`mc-dance-am-handships-dance-pumparms-01-loop`). The promotion build renames
// as it assembles the library; see tools/build/ft3d-avatar-emote-library.mjs.
//
// Server twin for the emote KEYS: ft_chat_voiced_emotes() in includes/functions/chat/ft3d_chat_inc.php.
// Server twin for the dance KEYS: ft_action_bar_dance_keys() in includes/functions/db/action_bars/action_bar_settings_inc.php.

export interface FtEmoteClipSpec {
    /** Clip names to try, in order. */
    candidates: readonly string[];
    /** A held pose / repeating motion (a dance) rather than a single gesture. */
    loop?: boolean;
    /** The emote is the character's DANCE PLAYLIST, performed through danceProgram(). */
    dance?: boolean;
}

export const EMOTE_CLIPS: Record<string, FtEmoteClipSpec> = {
    wave: { candidates: ['Emote_Wave'] },
    point: { candidates: ['Emote_Point'] },
    taunt: { candidates: ['Emote_TauntBattlecry', 'Emote_TauntChestThump'] },
    // "Get a load of this one" — the third taunt, its own command so it is reachable (owner batch 2).
    mock: { candidates: ['Emote_ThisGuy', 'Emote_TauntChestThump'] },
    charge: { candidates: ['Emote_TauntBattlecry'] },
    cheer: { candidates: ['Emote_Excited', 'Emote_HeelClick'] },
    // Jumping for joy until the player moves.
    celebrate: { candidates: ['Emote_Excited_Loop', 'Emote_Excited'], loop: true },
    // Three courtesies, owner 2026-09-16: a formal bow, a quick informal one, and the hat tip.
    bow: { candidates: ['Emote_BowFormal', 'Emote_BowInformal', 'Emote_HatTip'] },
    quickbow: { candidates: ['Emote_BowInformal', 'Emote_BowFormal', 'Emote_HatTip'] },
    hattip: { candidates: ['Emote_HatTip'] },
    // Applause loops until the player moves; the source clip is a single 1.17 s clap cycle.
    clap: { candidates: ['Emote_Clap_Loop'], loop: true },
    salute: { candidates: ['Emote_Salute'] },
    // A firm yes, and a slower, repeated nod of agreement.
    nod: { candidates: ['Emote_NodHard', 'Emote_NodLengthy'] },
    agree: { candidates: ['Emote_NodLengthy', 'Emote_NodHard'] },
    shrug: { candidates: ['Emote_Shrug'] },
    laugh: { candidates: ['Emote_Laugh', 'Emote_Excited'] },
    sigh: { candidates: ['Emote_Frustrated', 'Emote_Sad'] },
    despair: { candidates: ['Emote_FrustratedGrabHead', 'Emote_Frustrated'] },
    stomp: { candidates: ['Emote_FrustratedStomp', 'Emote_Frustrated'] },
    no: { candidates: ['Emote_Frustrated'] },
    bye: { candidates: ['Emote_Wave'] },
    hold: { candidates: ['Emote_Point'] },
    follow: { candidates: ['Emote_Wave'] },
    // A wounded wave for help, held until the player moves.
    heal: { candidates: ['Emote_InjuredWave_Loop'], loop: true },
    talk: { candidates: ['Emote_Talk'] },
    converse: { candidates: ['Emote_Converse_Loop', 'Emote_Talk'], loop: true },
    listen: { candidates: ['Emote_Listen_Loop'], loop: true },
    sniff: { candidates: ['Emote_StinkyPits_Loop'], loop: true },
    stinky: { candidates: ['Emote_StinkyBum_Loop'], loop: true },
    // A warning and a greedy look are both a point (owner 2026-09-17).
    trap: { candidates: ['Emote_Point'] },
    loot: { candidates: ['Emote_Point'] },
    dance: { candidates: [], loop: true, dance: true },
    // Everyday life (owner 2026-10-09): the Medieval NPC pack, in the LAZY ambient library (ft3d.avatar-clips.ambient.*).
    drink: { candidates: ['Ambient_DrinkAle_Loop'], loop: true },
    eat: { candidates: ['Ambient_EatBread_Loop'], loop: true },
    pray: { candidates: ['Ambient_Prayer_Loop'], loop: true },
    sleep: { candidates: ['Ambient_Sleep_Loop'], loop: true },
    sit: { candidates: ['Ambient_SitGround_Loop'], loop: true },
    sharpen: { candidates: ['Ambient_SharpenSword_Loop'], loop: true },
    hammer: { candidates: ['Ambient_BlacksmithHammer_Loop'], loop: true },
    polish: { candidates: ['Ambient_CleanRag_Loop'], loop: true },
    dust: { candidates: ['Ambient_BrushOffDust'] },
    fan: { candidates: ['Ambient_FanSelf_Loop'], loop: true },
    lean: { candidates: ['Ambient_LeanWall_Loop'], loop: true },
    coin: { candidates: ['Ambient_PlayCoin_Loop'], loop: true },
    guard: { candidates: ['Ambient_StandGuard_Loop'], loop: true },
    fidget: { candidates: ['Ambient_StandNervous_Loop'], loop: true },
    warm: { candidates: ['Ambient_WarmHands_Loop'], loop: true },
    tend: { candidates: ['Ambient_TendWound_Loop'], loop: true },
    scent: { candidates: ['Ambient_SniffAir_Loop'], loop: true },
    strum: { candidates: ['Ambient_EmoteGuitar'] },
    stagger: { candidates: ['Ambient_WalkStumbleDrunk_Loop'], loop: true },
};

/** The lazy library an emote clip lives in, or null for the always-loaded emote library. */
export function lazyEmoteLibraryFor(name: string): 'ambient' | 'dances' | null {
    return name.startsWith('Ambient_') ? 'ambient' : isLazyEmoteClip(name) ? 'dances' : null;
}

/**
 * Clips that live in the LAZY library (`ft3d.avatar-clips.dances.*`), fetched the first time a character
 * needs one: every dance and transition, plus the long conversation loops. Mirror of the `library`
 * column in tools/build/ft3d-avatar-emote-library.mjs.
 */
export function isLazyEmoteClip(name: string): boolean {
    return /^Emote_(Dance|Trans)_/.test(name) || name === 'Emote_Talk' || name === 'Emote_Converse_Loop' || name === 'Emote_Listen_Loop'
        || name.startsWith('Ambient_');
}

/** A dance's resting pose between clips. The "hands on hips" family begins and ends with hands on hips. */
export type FtDancePose = 'stand' | 'hips';

export interface FtDanceSpec {
    key: string;
    label: string;
    clip: string;
    pose: FtDancePose;
    /** Played once before the loop (from `pose`). */
    enter?: string;
    /** Played once after the loop when the playlist moves on (ends in `pose`). */
    exit?: string;
}

/** The chaining primitive: the pose change between two dances. */
export const DANCE_TRANSITIONS: Record<string, string> = {
    'stand>hips': 'Emote_Trans_StandToHandsHips',
    'hips>stand': 'Emote_Trans_HandsHipsToStand',
};

/** A playlist longer than this is trimmed (server twin: FT_ACTION_BAR_DANCE_PLAYLIST_MAX). */
export const DANCE_PLAYLIST_MAX = 12;
/** How long a multi-dance playlist lingers on each dance, rounded to whole loops of it. */
export const DANCE_HOLD_SECONDS = 12;

/**
 * Every dance, in picker order. The first entry is the default for a character with no playlist.
 * Mixamo rows mirror tools/build/ft3d-mixamo-dances.mjs (tools/test/ft3d-dance-catalog.test.mjs keeps
 * the two in step). ⛔ Labels are player-facing: no song, film or artist names.
 */
export const DANCE_CLIPS: readonly FtDanceSpec[] = [
    { key: 'groovy', label: 'Groovy', clip: 'Emote_Dance_Groovy_Loop', pose: 'stand', enter: 'Emote_Dance_Groovy_Start', exit: 'Emote_Dance_Groovy_Stop' },
    { key: 'sprinkler', label: 'Sprinkler', clip: 'Emote_Dance_Sprinkler_Loop', pose: 'stand' },
    { key: 'chicken', label: 'Chicken Wing', clip: 'Emote_Dance_ChickenWing_Loop', pose: 'stand' },
    { key: 'kick', label: 'Legs Kick', clip: 'Emote_Dance_LegsKick_Loop', pose: 'stand' },
    { key: 'pump', label: 'Pump Arms', clip: 'Emote_Dance_PumpArms_Loop', pose: 'hips', enter: 'Emote_Dance_PumpArms_Start', exit: 'Emote_Dance_PumpArms_Stop' },
    { key: 'freestyle', label: 'Freestyle', clip: 'Emote_Dance_Freestyle_Loop', pose: 'hips' },
    { key: 'hipskick', label: 'Hip Kicks', clip: 'Emote_Dance_HipsKick_Loop', pose: 'hips', enter: 'Emote_Dance_HipsKick_Start', exit: 'Emote_Dance_HipsKick_Stop' },
    { key: 'hipskick2', label: 'Hip Kicks II', clip: 'Emote_Dance_HipsKickAlt_Loop', pose: 'hips', enter: 'Emote_Dance_HipsKick_Start', exit: 'Emote_Dance_HipsKick_Stop' },
    { key: 'looktoside', label: 'Side Glance', clip: 'Emote_Dance_LookToSide_Loop', pose: 'hips', enter: 'Emote_Dance_LookToSide_Start', exit: 'Emote_Dance_LookToSide_Stop' },
    ...([
        ['barnyard', 'Barnyard Flap'], ['cancan', 'Can-Can'], ['turnabout', 'Turn About'], ['charleston', 'Charleston'],
        ['swing', 'Swing'], ['salsa', 'Salsa'], ['rumba', 'Rumba'], ['samba', 'Samba'], ['belly', 'Belly Dance'],
        ['silly', 'Silly'], ['twist', 'Twist'], ['jazz', 'Jazz'], ['ballet', 'Ballet'], ['soulspin', 'Soul Spin'],
        ['boogaloo', 'Boogaloo'], ['robot', 'Robot'], ['backslide', 'Backslide'], ['runningman', 'Running Man'],
        ['shuffle', 'Shuffle'], ['raiseroof', 'Raise the Roof'], ['horserider', 'Horse Rider'], ['hipsway', 'Hip Sway'],
        ['letters', 'Letters'], ['ghoul', 'Ghoul Shuffle'], ['bodywave', 'Body Wave'],
    ] as const).map(([key, label]): FtDanceSpec => ({
        key, label, clip: `Emote_Dance_${key.charAt(0).toUpperCase()}${key.slice(1)}_Loop`, pose: 'stand',
    })),
];

/** One step of a clip program: a clip held for `seconds` (Infinity = until interrupted). */
export interface FtClipStep {
    clip: string;
    loop: boolean;
    seconds: number;
}

/** A dance playlist sanitised the way the server stores it: known keys only, capped. */
export function normalizeDancePlaylist(raw: unknown): string[] {
    const list = Array.isArray(raw) ? raw : typeof raw === 'string' && raw !== '' ? raw.split(',') : [];
    const known = new Set(DANCE_CLIPS.map(entry => entry.key));
    return list.map(key => String(key).trim().toLowerCase()).filter(key => known.has(key)).slice(0, DANCE_PLAYLIST_MAX);
}

/**
 * THE CHAINING (owner 2026-09-16: an ordered playlist). Turns a playlist into a clip program the avatar
 * runtime plays step by step.
 *
 *  - One dance: [pose transition] → [its entry] → its loop, held until the dancer moves.
 *  - Several: for each, [pose transition] → [entry] → loop for ~DANCE_HOLD_SECONDS of whole cycles →
 *    [exit]; then a transition back to the first dance's pose, and the whole list repeats.
 *
 * `duration(clip)` answers the clip's length when this rig has it, or null. A dance whose LOOP is
 * missing is skipped entirely; a missing entry, exit or transition is skipped on its own (the fade
 * still carries the body across). An empty or all-missing playlist falls back to the first dance the
 * rig has, which is the pre-picker behaviour.
 */
export function danceProgram(playlist: readonly string[], duration: (clip: string) => number | null): { steps: FtClipStep[]; repeat: boolean; loopFrom: number } | null {
    const byKey = new Map(DANCE_CLIPS.map(entry => [entry.key, entry]));
    const present = (clip?: string): clip is string => !!clip && (duration(clip) ?? 0) > 0;
    let dances = normalizeDancePlaylist(playlist).map(key => byKey.get(key)!).filter(entry => present(entry.clip));
    if (dances.length === 0) {
        const fallback = DANCE_CLIPS.find(entry => present(entry.clip));
        if (!fallback) return null;
        dances = [fallback];
    }
    const steps: FtClipStep[] = [];
    const once = (clip?: string): void => { if (present(clip)) steps.push({ clip, loop: false, seconds: duration(clip)! }); };
    const single = dances.length === 1;
    let pose: FtDancePose = 'stand';
    // Where a repeat wraps to: after the OPENING transition out of the standing pose, which the end of the
    // list never returns to (it returns to the first dance's pose instead).
    let loopFrom = 0;
    for (const [i, dance] of dances.entries()) {
        if (dance.pose !== pose) once(DANCE_TRANSITIONS[`${pose}>${dance.pose}`]);
        if (i === 0) loopFrom = steps.length;
        pose = dance.pose;
        once(dance.enter);
        const cycle = duration(dance.clip)!;
        steps.push({
            clip: dance.clip,
            loop: true,
            seconds: single ? Infinity : Math.max(1, Math.round(DANCE_HOLD_SECONDS / cycle)) * cycle,
        });
        if (!single) once(dance.exit);
    }
    const firstPose = dances[0]!.pose;
    if (!single && pose !== firstPose) once(DANCE_TRANSITIONS[`${pose}>${firstPose}`]);
    return { steps, repeat: !single, loopFrom };
}

/** The clip spec for an emote key, or null for an emote with no mapping at all. */
export function emoteClipSpec(emote: string): FtEmoteClipSpec | null {
    const key = String(emote || '').trim().toLowerCase();
    if (!key) return null;
    return Object.prototype.hasOwnProperty.call(EMOTE_CLIPS, key) ? EMOTE_CLIPS[key]! : null;
}
